import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { MailRegular, DocumentRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';
import type { Notification, SendManualNotificationRequest } from '../../../types';

export interface SendReturnCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxReturnId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  /** Blob name of the just-uploaded completed return (passed directly to avoid Cosmos timing issues) */
  attachmentBlobName?: string;
  /** Original file name of the uploaded document */
  attachmentFileName?: string;
  /** Return type to determine password hint */
  returnType?: 'individual' | 'business';
  sendNotificationMutation: UseMutationResult<Notification, Error, SendManualNotificationRequest>;
}

export function SendReturnCopyDialog({
  open,
  onOpenChange,
  taxReturnId,
  clientName,
  clientEmail,
  clientPhone,
  attachmentBlobName,
  attachmentFileName,
  returnType = 'individual',
  sendNotificationMutation,
}: SendReturnCopyDialogProps) {
  const [sent, setSent] = useState(false);

  const handleSend = () => {
    sendNotificationMutation.mutate(
      {
        taxReturnId,
        notificationType: 'completed_return_copy',
        recipientType: 'client',
        attachmentBlobName,
        attachmentFileName,
      },
      {
        onSuccess: () => {
          setSent(true);
        },
      }
    );
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset sent state after dialog closes
    setTimeout(() => setSent(false), 200);
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) handleClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DocumentRegular />
              Send Return Copy to Client
            </div>
          </DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {sent ? (
                <MessageBar intent="success">
                  <MessageBarBody>
                    Return copy notification sent to {clientName}!
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <>
                  <Text>
                    The final tax return has been uploaded. Would you like to send a copy to <strong>{clientName}</strong>?
                  </Text>

                  <div style={{
                    backgroundColor: 'var(--tl-bg-subtle)',
                    borderRadius: '8px',
                    padding: '16px',
                    fontSize: '13px',
                    lineHeight: '1.6',
                  }}>
                    <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                      The email will include:
                    </Text>
                    <ul style={{ margin: '0', paddingLeft: '20px' }}>
                      <li>A copy of the completed tax return (PDF attached)</li>
                      <li>The password for the document is the <strong>last 4 digits of their {returnType === 'business' ? 'EIN' : 'SSN'}</strong></li>
                      <li>They can check their refund status at <strong>irs.gov/refunds</strong></li>
                      <li>Refunds typically take around <strong>2-3 weeks</strong></li>
                      <li>If they need to make a payment, they can do so at <strong>irs.gov/payments</strong></li>
                    </ul>
                  </div>

                  {clientEmail ? (
                    <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                      <MailRegular style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Will be sent to: {clientEmail}
                      {clientPhone ? ` and SMS to ${clientPhone}` : ''}
                    </div>
                  ) : (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        No email address on file for this client. Please add an email address first.
                      </MessageBarBody>
                    </MessageBar>
                  )}

                  {sendNotificationMutation.isError && (
                    <MessageBar intent="error">
                      <MessageBarBody>
                        Failed to send notification. Please try again.
                      </MessageBarBody>
                    </MessageBar>
                  )}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {sent ? (
              <Button appearance="primary" onClick={handleClose}>
                Done
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={handleClose}>
                  Skip
                </Button>
                <Button
                  appearance="primary"
                  icon={sendNotificationMutation.isPending ? <Spinner size="tiny" /> : <MailRegular />}
                  disabled={!clientEmail || sendNotificationMutation.isPending}
                  onClick={handleSend}
                >
                  {sendNotificationMutation.isPending ? 'Sending...' : 'Send to Client'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
